import type { Translatable } from "app/pages/sales-intelligence/types";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import type { FilterCategories } from "./category";
import type { FilterInstanceType } from "./filterInstance";
import type { IFilterSearchKeywordsConfig } from "./misc";
import type { SearchFilterInstance } from "./searchFilterInstance";

/**
 * A filter's *usage mode* — a property of the filter's contract, not of a page. `leadScores` cannot feed a
 * scoring rule (circular); `keywords`/`internationalFilters` are not evaluable by the scoring engine. Those
 * are facts about the filter, so the filter declares them; a new surface that evaluates one of these modes
 * needs no filter change. Kept page-free on purpose: the filter never names a page (ADR-0005/0008).
 */
export const filterUsageMap = {
    search: "search",
    scoringRule: "scoring-rule",
    territory: "territory",
} as const;

export type FilterUsage = (typeof filterUsageMap)[keyof typeof filterUsageMap];

export const ALL_FILTER_USAGES: readonly FilterUsage[] = Object.values(filterUsageMap);

/** Presentation of a pending placeholder card; the framework supplies its model and views (`createPendingInstance`). */
export type FilterPendingParts = Pick<FilterInstanceType<IPlainObject>, "tooltipText" | "tag" | "visibility"> & {
    readonly bodyText: Translatable;
};

/**
 * The single source of truth for one filter. Its static facts (identity, placement, requirements) are
 * declared here, not recovered from the runtime assembly; `build(ctx)` is the only place models and views
 * live. Entity-agnostic: every entity noun (values, api category, context, data source, capability) is a
 * type parameter, so this stays in the framework tier (ADR-0006/0008).
 */
export type FilterDefinition<
    TValues extends IPlainObject,
    TApiCategory extends string,
    TCtx,
    TDataSource extends string,
    TCapability extends string,
    K extends Extract<keyof TValues, string> = Extract<keyof TValues, string>,
> = {
    /* identity — static, registration-free */
    readonly key: K;
    readonly apiCategory: TApiCategory;
    readonly category: FilterCategories;
    readonly trackingName: string;
    /** Defaults to `true`; `false` for refinement/exclusion filters (suppression list). */
    readonly affectsSearchDefinition?: boolean;

    /* static presentation — needed WITHOUT registration (chips, summaries, filter search) */
    readonly titleKey?: Translatable;
    readonly searchKeywords?: IFilterSearchKeywordsConfig;

    /* requirements — declared once, resolved centrally */
    readonly requires?: readonly TDataSource[];
    readonly requiresCapabilities?: readonly TCapability[];
    /**
     * Whether this filter seeds a non-empty initial DTO value *on this surface* — a default the surface
     * writes into state on first init. It is a predicate over `ctx` because the answer can be per-surface
     * (e.g. suppression only defaults when `applySuppressionListByDefault` is granted). Assembly holds the
     * whole config at `pending` while such a definition is selected but not yet buildable, so the one-shot
     * defaults seed never runs without it. `ctx` fields it reads must be surface intent, not fetched data —
     * it is evaluated before the definition can build.
     */
    readonly seedsDefault?: (ctx: TCtx) => boolean;
    /** Which surfaces may show this filter; defaults to all usages. */
    readonly usages?: readonly FilterUsage[];

    /* runtime construction — models and views live here, and ONLY here */
    readonly build: (
        ctx: TCtx,
    ) =>
        | SearchFilterInstance<TValues, TApiCategory>
        | readonly SearchFilterInstance<TValues, TApiCategory>[]
        | undefined;

    /**
     * Opt-in loading placeholder shown while every missing `requires` source is in flight. Runs on the
     * WIDE ctx (like `seedsDefault`) — read only surface/session intent, never fetched data. Must mirror
     * any veto inside `build` (return undefined), else the placeholder shows for a filter that never
     * appears. Per-definition opt-in on purpose: first case is industry v2 — re-evaluate if more filters
     * or a whole-panel treatment need it.
     */
    readonly buildPending?: (ctx: TCtx) => FilterPendingParts | undefined;
};

/**
 * The runtime half of a built instance: everything `build` genuinely varies — the model, the views, and
 * optional per-instance chrome (tag, gate, tooltip). Identity (`key`/`apiCategory`/`category`/`trackingName`)
 * is injected from the definition by {@link defineFilter}, so it is declared exactly once.
 */
export type FilterInstanceParts<TValues extends IPlainObject, TApiCategory extends string> = Omit<
    SearchFilterInstance<TValues, TApiCategory>,
    "key" | "apiCategory" | "category" | "trackingName"
>;

/**
 * What a surface grants, in the same page-free vocabulary a definition requires. A surface never names a
 * filter key; a definition never names a page. Resolution is set containment ({@link selectDefinitions}).
 */
export type SurfaceSelection<TCategory extends string, TCapability extends string> = {
    readonly usage: FilterUsage;
    /** Undefined = every category is in scope. */
    readonly categories?: ReadonlySet<TCategory>;
    readonly grants: ReadonlySet<TCapability>;
};
